import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FilePreviewDialogComponent } from 'src/app/components/dialogs/file-preview-dialog/file-preview-dialog.component';
import { ProcessHandlerService } from 'src/app/process-engine/process/process-handler-dialog/process-handler.service';
import { BackendService } from 'src/app/services/backend.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ApiRequests, components } from 'src/app/shared/types/api.type';

@Component({
  selector: 'app-document-review-notification-panel',
  templateUrl: './document-review-notification-panel.component.html',
  styleUrls: ['./document-review-notification-panel.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatIconModule
  ]
})
export class DocumentReviewNotificationPanelComponent {
  @Input({ required: true }) notification!: components['schemas']['InAppNotification'];
  @Output() readonly resolved = new EventEmitter<void>();

  decision: 'accepted' | 'rejected' = 'accepted';
  rejectionReason = '';
  submitting = false;
  previewing = false;

  constructor(
    private readonly backendService: BackendService,
    private readonly notify: NotificationService,
    private readonly translate: TranslocoService,
    private readonly dialog: MatDialog,
    private readonly processHandlerService: ProcessHandlerService
  ) {}

  get metadata(): components['schemas']['DocumentReviewNotificationMetadataDto'] {
    return (this.notification.metadata as unknown as components['schemas']['DocumentReviewNotificationMetadataDto'] | undefined) ?? {
      reviewId: 0,
      userName: '',
      subtypeName: '',
      status: 'rejected'
    };
  }

  get isAdminReview(): boolean {
    return this.notification.requiresAction && this.metadata.status === 'pending';
  }

  /** The system closed the review itself, so the reason is a code and needs its own wording. */
  get systemReason(): 'deleted' | null {
    return this.metadata.systemReason ?? null;
  }

  get documentDeleted(): boolean {
    return !!this.metadata.documentDeletedAt;
  }

  get canOpenFile(): boolean {
    if (this.documentDeleted || this.systemReason) return false;
    return this.metadata.status !== 'rejected' || this.isAdminReview;
  }

  openPreview(): void {
    this.previewing = true;
    this.backendService.post<'auth/generateNonce'>('auth/{id}/nonce', { path: { id: this.notification.id } }).subscribe({
      next: (res) => {
        this.backendService
          .getBlob<'document/getReviewFile'>('document/reviews/{id}/file', {
            path: { id: this.notification.id },
            query: { nonce: res.nonce }
          })
          .subscribe({
            next: (blob) => {
              this.previewing = false;
              const url = URL.createObjectURL(blob);
              this.dialog.open(FilePreviewDialogComponent, {
                ...this.processHandlerService.getDialogScreenSize(),
                data: {
                  url,
                  file: { type: blob.type || 'application/octet-stream', name: this.metadata.originalName || this.metadata.subtypeName }
                }
              });
            },
            error: () => {
              this.previewing = false;
            }
          });
      },
      error: () => {
        this.previewing = false;
      }
    });
  }

  submit(): void {
    if (!this.isAdminReview) return;
    if (this.decision === 'rejected' && !this.rejectionReason.trim()) {
      this.notify.error(this.translate.translate('notifications.document_review.rejection_reason_required'));
      return;
    }

    const body: ApiRequests['document/resolveReview'] = {
      accepted: this.decision === 'accepted',
      rejectionReason: this.decision === 'rejected' ? this.rejectionReason.trim() : undefined
    };

    this.submitting = true;
    this.backendService
      .post<'document/resolveReview'>('document/reviews/{id}/resolve', {
        path: { id: this.notification.id },
        body
      })
      .subscribe({
        next: () => {
          this.submitting = false;
          this.notify.success(this.translate.translate('notifications.document_review.resolved'));
          this.resolved.emit();
        },
        error: () => {
          this.submitting = false;
        }
      });
  }
}
